import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import Breadcrumb from "../Breadcrumb";
import Modal from "../Modal/Modal";
import { logoutUser } from "../../actions/user";
import { validateName, validateEmail } from "../../helpers/validation";
import "./UserEdit.css"

const UserEdit = React.memo(({
    isFetching,
    detail,
    fetchUserDetail,
    fetchUserInfo,
    user_info,
    updateUser     
}) => {

    const { userId } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMessage, setModalMessage] = useState("");
    const [modalConfirmAction, setModalConfirmAction] = useState(() => () => {});

    const [form, setForm] = useState({
        login_id: "",
        user_name: "",
    });

    const [errors, setErrors] = useState({
        login_id: "",
        user_name: "",
    });
    const [isLocked, setIsLocked] = useState(false);
    const [wrongOtpCount, setWrongOtpCount] = useState(0);

    // 初期ロード時にユーザー情報と管理者詳細を取得
    useEffect(() => {
        fetchUserInfo();
        fetchUserDetail(userId);
    // eslint-disable-next-line
    }, [userId]);

    // 詳細データ取得後、フォームに反映
    useEffect(() => {
        if (detail) {
            setForm({
                login_id: detail.login_id || "",
                user_name: detail.user_name || "",
            });
            setIsLocked(detail.user_status === 2 || detail.user_status === 1);
            setWrongOtpCount(detail.wrong_otp_count || 0);
        }
    }, [detail]);

    const handleChange = (e) => {
        const { name, value } = e.target;

        setForm({ ...form, [name]: value });

        if (errors[name]) {
            setErrors({ ...errors, [name]: "" });
        }
    };

    // 更新処理
    const handleUpdate = async () => {
        // Validate
        const newErrors = {};

        const loginError = validateEmail(form.login_id);
        if (loginError) newErrors.login_id = loginError;

        const nameError = validateName(form.user_name);
        if (nameError) newErrors.user_name = nameError;

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        // Clear errors
        setErrors({
            login_id: "",
            user_name: "",
        });

        const payload = {
            user_id: userId,
            login_id: form.login_id,
            user_name: form.user_name,
            authority_class: 0,
            wrong_otp_count: wrongOtpCount,
        };

        const result = await updateUser(payload);

        if (result?.payload) {
            navigate("/user-list");
            return;
        }

        setModalMessage(result?.error || "更新に失敗しました。");
        setModalConfirmAction(() => () => setIsModalOpen(false));
        setIsModalOpen(true);
    };

        // ロック / 解除 処理
    const handleToggleLock = async () => {
        const newStatus = isLocked ? 0 : 2;

        const payload = {
            user_id: userId,
            login_id: form.login_id,
            user_name: form.user_name,
            user_status: newStatus,
            authority_class: 0,
            wrong_otp_count: isLocked ? 0 : wrongOtpCount,
        };

        const result = await updateUser(payload);

        if (result?.payload) {
            setIsLocked(!isLocked);
            if (isLocked) {
            // Unlock
            setWrongOtpCount(0);
            }

            setModalMessage(isLocked ? "ユーザーを解除しました。" : "ユーザーをロックしました。");
            setModalConfirmAction(() => () => setIsModalOpen(false));
            setIsModalOpen(true);

            return;
        }

        setModalMessage(result?.error || "更新に失敗しました。");
        setModalConfirmAction(() => () => setIsModalOpen(false));
        setIsModalOpen(true);
    };


    // 削除処理
    const handleDelete = () => {
        setModalMessage("本当に削除しますか？");
        setModalConfirmAction(() => async () => {
            setIsModalOpen(false);

            const result = await updateUser({
                user_id: userId,
                login_id: form.login_id,
                user_name: form.user_name,
                user_status: 1,
                authority_class: 0,
                wrong_otp_count: wrongOtpCount,
            });

            if (result?.payload !== undefined) {
                navigate("/user-list");
                return;
            }

            setModalMessage(result?.error || "削除に失敗しました。");
            setModalConfirmAction(() => () => setIsModalOpen(false));
            setIsModalOpen(true);
        });

        setIsModalOpen(true);
    };
    return (
        <div id="Main">
            <div id="Contents">

                <Breadcrumb 
                    user={user_info?.admin_name}
                    onLogout={() => dispatch(logoutUser())} 
                />

                {/* モーダル */}
                {isModalOpen && (
                    <Modal
                        isOpen={isModalOpen}
                        onClose={() => setIsModalOpen(false)}
                        message={modalMessage}
                        onConfirm={modalConfirmAction}
                        showCancelButton={modalMessage === "本当に削除しますか？"}
                    />
                )}

                <div className="user-layout">
                    <div className="user-card">
                        <h2 className="user-title">ユーザー編集</h2>

                        <div className="user-edit-btn-row">
                            <div className="btn-row-right">
                                <button className="btn-update" onClick={handleUpdate} disabled={isLocked}>更新</button>
                                <button className="btn-lock" onClick={handleToggleLock}>
                                    {isLocked ? "解除" : "ロック"}
                                </button>

                                <button className="btn-delete" onClick={handleDelete}>
                                    削除
                                </button>
                                <button className="btn-back" onClick={() => navigate("/user-list")}>
                                    戻る
                                </button>
                            </div>
                        </div>

                        {/* Form */}
                        <div className="user-edit-form-box">
                            {/* LOGIN ID */}
                            <div className="form-row">
                                <label>ログインID</label>
                                <input
                                    name="login_id"
                                    value={form.login_id}
                                    onChange={handleChange}
                                    className="user-edit-input user-edit-input-disabled"
                                    disabled
                                />
                            </div>
                            {errors.login_id && <p className="error-text">{errors.login_id}</p>}

                            {/* USER NAME */}
                            <div className="form-row">
                                <label>ユーザー名</label>
                                <input
                                    name="user_name"
                                    value={form.user_name}
                                    onChange={handleChange}
                                    className="user-edit-input"
                                />
                            </div>
                            {errors.user_name && <p className="error-text">{errors.user_name}</p>}

                        </div>

                    </div>
                </div>
            </div>

            <div className={`overlay ${isFetching ? 'active' : ''}`}>
                <div className={`spinner ${isFetching ? 'active' : ''}`}></div>
            </div>
        </div>
    );
});

UserEdit.displayName = "UserEdit";    // fix eslint(react/display-name)

UserEdit.propTypes = {
    isFetching: PropTypes.bool.isRequired,
    detail: PropTypes.object,
    error: PropTypes.any,
    fetchUserDetail: PropTypes.func.isRequired,
    fetchUserInfo: PropTypes.func.isRequired,
    updateUser: PropTypes.func.isRequired,
    user_info: PropTypes.object,
};

export default UserEdit;